import { useQueryClient } from '@tanstack/react-query';
import { EngineStatusBadge, EngineUpdatesModal, useEngineUpdates } from '../components/stores/EngineUpdates';
import { Button } from '../components/ui/Button';
import { useState, useEffect } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import {
  Store,
  Plus,
  RefreshCw,
  Trash2,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader2,
  ShoppingBag,
  Globe,
  Key,
  X,
  Settings,
  Edit3,
  Truck,
  Download,
  PlusCircle,
  Package,
  ArrowRight,
  ArrowLeftRight,
  Ban,
  Copy,
  Puzzle,
} from 'lucide-react';
import api, { storeApi, engineApi, warehouseApi, type Store as StoreType, type CreateStoreRequest, type ShippingMethodMapping } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';

export function StoresPage() {
  const { showSuccess, showError } = useNotification();
  const confirmDialog = useConfirmDialog();
  const [stores, setStores] = useState<StoreType[]>([]);
  const updates = useEngineUpdates(stores);
  const [showUpdates, setShowUpdates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreType | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState<StoreType | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const { data } = await storeApi.getAll();
      setStores(data);
    } catch (err) {
      setError('Nie udało się załadować sklepów');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (storeId: string) => {
    try {
      setSyncing(storeId);
      const { data } = await storeApi.sync(storeId);
      showSuccess('sync', { message: data.message || `Synchronizacja zakończona! Utworzono: ${data.created}, Zaktualizowano: ${data.updated}` });
      loadStores();
    } catch (err) {
      showError(err, 'sync');
    } finally {
      setSyncing(null);
    }
  };

  const handleDelete = async (storeId: string) => {
    if (!(await confirmDialog({
      title: 'Usuń sklep',
      message: 'Czy na pewno chcesz usunąć ten sklep i jego zamówienia? Zarchiwizowana historia Finansów pozostanie do czasu usunięcia firmy.',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await storeApi.delete(storeId);
      loadStores();
    } catch (err) {
      showError(err, 'store');
    }
  };

  const handleToggleActive = async (store: StoreType) => {
    try {
      await storeApi.update(store.id, { isActive: !store.isActive });
      loadStores();
    } catch (err) {
      showError(err, 'store');
    }
  };

  return (
    <>
      <EngineUpdatesModal isOpen={showUpdates} onClose={() => setShowUpdates(false)} stores={stores} updates={updates} />
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-wrap gap-4 items-center justify-between animate-fade-in">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Sklepy</h1>
            <p className="text-text-secondary mt-2">Zarządzaj połączonymi sklepami WooCommerce</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" leftIcon={<RefreshCw />} onClick={() => { void updates.refresh(); setShowUpdates(true); }}>
              {updates.busy ? 'Aktualizowanie...' : 'Aktualizuj'}
            </Button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary"
          >
            <Plus className="w-5 h-5 mr-2" />
            Dodaj sklep
          </button>
          </div>
        </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loader" />
        </div>
      ) : stores.length === 0 ? (
        <div className="animate-slide-up">
          <EmptyState
            icon={Store}
            title="Brak sklepów"
            description="Dodaj swój pierwszy sklep WooCommerce, aby rozpocząć synchronizację zamówień."
            buttonLabel="Dodaj pierwszy sklep"
            onButtonClick={() => setShowAddModal(true)}
            buttonIcon={Plus}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {stores.map((store, index) => (
            <div
              key={store.id}
              className="card animate-slide-up group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-4 min-w-0 max-w-full">
                  <div className={`p-3 rounded-xl ${
                    store.isActive
                      ? 'bg-gradient-to-br from-accent-primary to-accent-secondary'
                      : 'bg-surface-hover'
                  }`}>
                    <ShoppingBag className={`w-6 h-6 ${store.isActive ? 'text-dark-900' : 'text-text-muted'}`} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-text-primary break-words">{store.name}</h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`badge ${store.isActive ? 'badge-success' : 'badge-error'}`}>
                        {store.isActive ? 'Aktywny' : 'Nieaktywny'}
                      </span>
                      <span className="badge badge-info">WooCommerce</span>
                      <EngineStatusBadge store={store} status={updates.statuses[store.id]} checking={updates.checking[store.id]} />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSync(store.id)}
                    disabled={syncing === store.id || !store.isActive}
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all disabled:opacity-50"
                    title="Synchronizuj"
                  >
                    {syncing === store.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => setEditingStore(store)}
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-primary hover:bg-surface-hover transition-all"
                    title="Edytuj"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowSettingsModal(store)}
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-secondary hover:bg-surface-hover transition-all"
                    title="Ustawienia integracji"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                  <a
                    href={store.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-accent-secondary hover:bg-surface-hover transition-all"
                    title="Otwórz sklep"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </a>
                  <button
                    onClick={() => handleDelete(store.id)}
                    className="p-2 rounded-lg bg-surface-hover/50 text-text-secondary hover:text-danger hover:bg-danger/10 transition-all"
                    title="Usuń"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                  <span className="text-sm text-text-secondary">URL sklepu</span>
                  <span className="text-sm text-text-primary truncate max-w-[200px]">{store.url}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                  <span className="text-sm text-text-secondary">Zamówienia</span>
                  <span className="text-sm text-accent-primary font-semibold">{store._count?.orders || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-surface-hover/30">
                  <span className="text-sm text-text-secondary">Ostatnia synchronizacja</span>
                  <span className="text-sm text-text-primary">
                    {store.lastSyncAt
                      ? new Date(store.lastSyncAt).toLocaleString('pl-PL')
                      : 'Nigdy'}
                  </span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-border-subtle flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer">
                  <div
                    onClick={() => handleToggleActive(store)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      store.isActive ? 'bg-success' : 'bg-surface-hover'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        store.isActive ? 'left-7' : 'left-1'
                      }`}
                    />
                  </div>
                  <span className="text-sm text-text-secondary">
                    {store.isActive ? 'Sklep aktywny' : 'Sklep nieaktywny'}
                  </span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Modals - outside of space-y-8 container */}
      {showAddModal && (
        <AddStoreModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            loadStores();
          }}
        />
      )}

      {editingStore && (
        <EditStoreModal
          store={editingStore}
          onClose={() => setEditingStore(null)}
          onSuccess={() => {
            setEditingStore(null);
            loadStores();
          }}
        />
      )}

      {showSettingsModal && (
        <StoreSettingsModal
          store={showSettingsModal}
          onClose={() => setShowSettingsModal(null)}
          onSuccess={() => {
            setShowSettingsModal(null);
            loadStores();
          }}
        />
      )}
    </>
  );
}

function AddStoreModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState<CreateStoreRequest>({
    name: '',
    url: '',
    apiKey: '',
    apiSecret: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await storeApi.create(formData);
      onSuccess();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się dodać sklepu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center animate-fade-in">
      <div className="modal max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Dodaj sklep WooCommerce</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
              <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="label">
              <Store className="w-4 h-4 inline mr-2" />
              Nazwa sklepu
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="Mój Sklep"
            />
          </div>

          <div>
            <label className="label">
              <Globe className="w-4 h-4 inline mr-2" />
              URL sklepu
            </label>
            <input
              type="url"
              required
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="input"
              placeholder="https://mojsklep.pl"
            />
          </div>

          <div>
            <label className="label">
              <Key className="w-4 h-4 inline mr-2" />
              Consumer Key (API Key)
            </label>
            <input
              type="text"
              required
              value={formData.apiKey}
              onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
              className="input"
              placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div>
            <label className="label">
              <Key className="w-4 h-4 inline mr-2" />
              Consumer Secret (API Secret)
            </label>
            <input
              type="password"
              required
              value={formData.apiSecret}
              onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
              className="input"
              placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
          </div>

          <div className="p-4 bg-accent-primary/10 border border-accent-primary/30 rounded-xl">
            <h4 className="text-sm font-medium text-accent-primary mb-2">Jak uzyskać klucze API?</h4>
            <ol className="text-sm text-text-secondary space-y-1 list-decimal list-inside">
              <li>Zaloguj się do panelu WordPress</li>
              <li>Przejdź do WooCommerce → Ustawienia → Zaawansowane → REST API</li>
              <li>Kliknij "Dodaj klucz" i skopiuj Consumer Key oraz Secret</li>
            </ol>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Łączenie...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Dodaj sklep
                </span>
              )}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditStoreModal({
  store,
  onClose,
  onSuccess,
}: {
  store: StoreType;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    name: store.name,
    url: store.url,
    apiKey: '',
    apiSecret: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const updateData: Record<string, string> = {
        name: formData.name,
        url: formData.url,
      };
      // Only include credentials if they were changed
      if (formData.apiKey) updateData.apiKey = formData.apiKey;
      if (formData.apiSecret) updateData.apiSecret = formData.apiSecret;

      await storeApi.update(store.id, updateData);
      onSuccess();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zaktualizować sklepu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center animate-fade-in">
      <div className="modal max-w-lg w-full mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Edytuj sklep</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger">
              <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="label">
              <Store className="w-4 h-4 inline mr-2" />
              Nazwa sklepu
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="Mój Sklep"
            />
          </div>

          <div>
            <label className="label">
              <Globe className="w-4 h-4 inline mr-2" />
              URL sklepu
            </label>
            <input
              type="url"
              required
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="input"
              placeholder="https://mojsklep.pl"
            />
          </div>

          <div className="p-4 bg-surface-hover/50 border border-border rounded-xl">
            <h4 className="text-sm font-medium text-text-secondary mb-3">Zmień klucze API (opcjonalnie)</h4>
            <p className="text-xs text-text-muted mb-4">Pozostaw puste, jeśli nie chcesz zmieniać kluczy API.</p>

            <div className="space-y-4">
              <div>
                <label className="label text-xs">
                  <Key className="w-3 h-3 inline mr-1" />
                  Nowy Consumer Key
                </label>
                <input
                  type="text"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  className="input !py-2 text-sm"
                  placeholder="ck_xxxxxxxx... (pozostaw puste, aby nie zmieniać)"
                />
              </div>

              <div>
                <label className="label text-xs">
                  <Key className="w-3 h-3 inline mr-1" />
                  Nowy Consumer Secret
                </label>
                <input
                  type="password"
                  value={formData.apiSecret}
                  onChange={(e) => setFormData({ ...formData, apiSecret: e.target.value })}
                  className="input !py-2 text-sm"
                  placeholder="cs_xxxxxxxx... (pozostaw puste, aby nie zmieniać)"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="btn-primary flex-1">
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Zapisywanie...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Zapisz zmiany
                </span>
              )}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              Anuluj
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Real WooCommerce statuses - Polish names with English in parentheses
const WOO_STATUSES = [
  { id: 'pending', name: 'Oczekujące (pending)' },
  { id: 'processing', name: 'W trakcie realizacji (processing)' },
  { id: 'on-hold', name: 'Wstrzymane (on-hold)' },
  { id: 'completed', name: 'Zrealizowane (completed)' },
  { id: 'cancelled', name: 'Anulowane (cancelled)' },
  { id: 'refunded', name: 'Zwrócone (refunded)' },
  { id: 'failed', name: 'Nieudane (failed)' },
];

// User-created status interface
interface EcomflowStatus {
  id: string;
  name: string;
  color: string;
  children?: EcomflowStatus[];
}

// Status mapping interface
interface StatusMapping {
  wooStatus: string;
  ecomflowStatus: string;
}

interface LocalStoreSettings {
  syncEnabled: boolean;
  syncFromDate: string;
  syncToStatus: string;
  syncStatuses: string[];
  statusMappings: StatusMapping[];
  defaultManualOrderStatusId: string | null;
  shippingMethodMappings: ShippingMethodMapping[];
}

// Paczkomat/courier keyword arrays for auto-suggestion
const PACZKOMAT_KEYWORDS = ['paczkomat', 'automat paczkowy', 'punkt odbioru', 'locker', 'parcel locker'];
const COURIER_KEYWORDS = ['kurier', 'courier', 'dostawa kurierem', 'przesyłka kurierska', 'door to door'];
const COD_KEYWORDS = ['pobranie', 'za pobraniem', 'cod', 'cash on delivery', 'przy odbiorze'];

function suggestInpostService(title: string): ShippingMethodMapping['inpostService'] {
  const lower = title.toLowerCase();
  if (COURIER_KEYWORDS.some(k => lower.includes(k))) return 'inpost_courier_standard';
  if (PACZKOMAT_KEYWORDS.some(k => lower.includes(k))) return 'inpost_locker_standard';
  return null;
}

function suggestIsCOD(title: string): boolean {
  const lower = title.toLowerCase();
  return COD_KEYWORDS.some(k => lower.includes(k));
}

const INPOST_SERVICE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Pomiń (nie InPost)' },
  { value: 'inpost_locker_standard', label: 'Paczkomat' },
  { value: 'inpost_courier_standard', label: 'Kurier standardowy' },
  { value: 'inpost_courier_express', label: 'Kurier express' },
];

function ShippingMappingTab({
  storeId,
  mappings,
  onMappingsChange,
}: {
  storeId: string;
  mappings: ShippingMethodMapping[];
  onMappingsChange: (mappings: ShippingMethodMapping[]) => void;
}) {
  const [loadingWoo, setLoadingWoo] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualMethodId, setManualMethodId] = useState('');
  const [manualMethodTitle, setManualMethodTitle] = useState('');

  const fetchFromWooCommerce = async () => {
    setLoadingWoo(true);
    setFetchError('');
    try {
      const res = await storeApi.getShippingZoneMethods(storeId);
      const existingIds = new Set(mappings.map(m => m.wooMethodId));
      const newMappings: ShippingMethodMapping[] = [];

      for (const method of res.data) {
        if (!existingIds.has(method.methodId)) {
          newMappings.push({
            wooMethodId: method.methodId,
            wooMethodTitle: method.title,
            inpostService: suggestInpostService(method.title),
            isCOD: suggestIsCOD(method.title),
          });
        }
      }

      if (newMappings.length > 0) {
        onMappingsChange([...mappings, ...newMappings]);
      } else if (res.data.length > 0) {
        setFetchError('Wszystkie metody wysyłki z WooCommerce są już dodane.');
      } else {
        setFetchError('Nie znaleziono metod wysyłki w strefach WooCommerce.');
      }
    } catch (err) {
      console.error('Failed to fetch shipping zone methods:', err);
      setFetchError('Nie udało się pobrać metod wysyłki z WooCommerce. Sprawdź połączenie ze sklepem.');
    } finally {
      setLoadingWoo(false);
    }
  };

  const fetchFromOrders = async () => {
    setLoadingOrders(true);
    setFetchError('');
    try {
      const res = await storeApi.getShippingMethodsFromOrders(storeId);
      const existingIds = new Set(mappings.map(m => m.wooMethodId));
      const newMappings: ShippingMethodMapping[] = [];

      for (const method of res.data) {
        if (!existingIds.has(method.methodId)) {
          newMappings.push({
            wooMethodId: method.methodId,
            wooMethodTitle: method.methodTitle,
            inpostService: suggestInpostService(method.methodTitle),
            isCOD: suggestIsCOD(method.methodTitle),
          });
        }
      }

      if (newMappings.length > 0) {
        onMappingsChange([...mappings, ...newMappings]);
      } else if (res.data.length > 0) {
        setFetchError('Wszystkie metody wysyłki z zamówień są już dodane.');
      } else {
        setFetchError('Nie znaleziono metod wysyłki w istniejących zamówieniach.');
      }
    } catch (err) {
      console.error('Failed to fetch shipping methods from orders:', err);
      setFetchError('Nie udało się pobrać metod wysyłki z zamówień.');
    } finally {
      setLoadingOrders(false);
    }
  };

  const updateMapping = (index: number, updates: Partial<ShippingMethodMapping>) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], ...updates };
    onMappingsChange(updated);
  };

  const removeMapping = (index: number) => {
    onMappingsChange(mappings.filter((_, i) => i !== index));
  };

  const addManualMapping = () => {
    if (!manualMethodId.trim()) return;
    const existingIds = new Set(mappings.map(m => m.wooMethodId));
    if (existingIds.has(manualMethodId.trim())) {
      setFetchError(`Metoda "${manualMethodId.trim()}" jest już dodana.`);
      return;
    }
    onMappingsChange([...mappings, {
      wooMethodId: manualMethodId.trim(),
      wooMethodTitle: manualMethodTitle.trim() || manualMethodId.trim(),
      inpostService: null,
      isCOD: false,
    }]);
    setManualMethodId('');
    setManualMethodTitle('');
    setShowManualAdd(false);
  };

  return (
    <div className="space-y-4">
      {/* Header with fetch buttons */}
      <div className="p-4 bg-surface-hover/30 rounded-xl">
        <h4 className="text-text-primary font-medium mb-2">Mapowanie metod wysyłki</h4>
        <p className="text-sm text-text-secondary mb-4">
          Powiąż metody wysyłki WooCommerce z usługami InPost. Dzięki temu system będzie wiedział,
          jaki typ przesyłki utworzyć dla każdego zamówienia.
        </p>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={fetchFromWooCommerce}
            disabled={loadingWoo}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-surface-muted hover:bg-surface-overlay text-text-primary rounded-lg transition-colors disabled:opacity-50"
          >
            {loadingWoo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Pobierz z WooCommerce
          </button>
          <button
            onClick={fetchFromOrders}
            disabled={loadingOrders}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-surface-muted hover:bg-surface-overlay text-text-primary rounded-lg transition-colors disabled:opacity-50"
          >
            {loadingOrders ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Pobierz z zamówień
          </button>
          <button
            onClick={() => setShowManualAdd(!showManualAdd)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-surface-muted hover:bg-surface-overlay text-text-primary rounded-lg transition-colors"
          >
            <PlusCircle className="w-4 h-4" />
            Dodaj ręcznie
          </button>
        </div>

        {fetchError && (
          <div className="flex items-center mt-3 p-3 bg-warning/10 border border-warning/30 rounded-lg text-warning text-sm">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            {fetchError}
          </div>
        )}

        {/* Manual add form */}
        {showManualAdd && (
          <div className="mt-3 p-3 bg-surface-muted/50 rounded-lg space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={manualMethodId}
                onChange={(e) => setManualMethodId(e.target.value)}
                placeholder="ID metody (np. flat_rate:2)"
                className="input flex-1 !py-2 text-sm"
              />
              <input
                type="text"
                value={manualMethodTitle}
                onChange={(e) => setManualMethodTitle(e.target.value)}
                placeholder="Nazwa (opcjonalna)"
                className="input flex-1 !py-2 text-sm"
              />
              <button
                onClick={addManualMapping}
                disabled={!manualMethodId.trim()}
                className="px-3 py-2 text-sm bg-accent-primary text-dark-900 rounded-lg disabled:opacity-50 font-medium"
              >
                Dodaj
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mapping table */}
      {mappings.length > 0 ? (
        <div className="space-y-2">
          {/* Table header */}
          <div className="grid grid-cols-[1fr_180px_80px_40px] gap-3 px-4 py-2 text-xs text-text-muted font-medium uppercase">
            <span>Metoda WooCommerce</span>
            <span>Usługa InPost</span>
            <span className="text-center">Pobranie</span>
            <span></span>
          </div>

          {mappings.map((mapping, index) => (
            <div key={mapping.wooMethodId} className="grid grid-cols-[1fr_180px_80px_40px] gap-3 items-center p-3 bg-surface-hover/30 rounded-lg">
              {/* WooCommerce method info */}
              <div className="min-w-0">
                <div className="text-sm text-text-primary truncate">{mapping.wooMethodTitle}</div>
                <div className="text-xs text-text-muted font-mono truncate">{mapping.wooMethodId}</div>
              </div>

              {/* InPost service select */}
              <select
                value={mapping.inpostService || ''}
                onChange={(e) => updateMapping(index, {
                  inpostService: e.target.value as ShippingMethodMapping['inpostService'] || null,
                })}
                className={`input !py-1.5 text-sm ${
                  mapping.inpostService ? 'border-accent-primary/30' : 'border-gray-600'
                }`}
              >
                {INPOST_SERVICE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* COD checkbox */}
              <div className="flex justify-center">
                <button
                  onClick={() => updateMapping(index, { isCOD: !mapping.isCOD })}
                  className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                    mapping.isCOD
                      ? 'bg-accent-primary border-accent-primary'
                      : 'border-gray-500 hover:border-gray-400'
                  }`}
                >
                  {mapping.isCOD && <CheckCircle className="w-4 h-4 text-dark-900" />}
                </button>
              </div>

              {/* Delete */}
              <button
                onClick={() => removeMapping(index)}
                className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Truck}
          title="Brak metod wysyłki"
          description="Użyj przycisków powyżej, aby pobrać metody wysyłki z WooCommerce lub z istniejących zamówień."
          size="sm"
        />
      )}

      {mappings.length > 0 && (
        <p className="text-xs text-text-muted">
          Metody oznaczone jako "Pomiń" nie będą obsługiwane przez InPost.
          Flaga "Pobranie" oznacza, że zamówienia z tą metodą wysyłki wymagają płatności za pobraniem.
          Pamiętaj, aby kliknąć "Zapisz ustawienia" po wprowadzeniu zmian.
        </p>
      )}
    </div>
  );
}

// ============ WAREHOUSE TAB ============

interface WarehouseSettingsData {
  productSyncEnabled: boolean;
  stockSyncDirection: 'none' | 'wc_to_ecom' | 'ecom_to_wc' | 'bidirectional';
  stockDecreaseStatusIds: string[];
  stockIncreaseStatusIds: string[];
}

interface StatusOption {
  id: string;
  name: string;
  color: string;
  parentId: string | null;
  storeId: string | null;
  store?: { id: string; name: string } | null;
}

interface StatusTreeNode extends StatusOption {
  children: StatusTreeNode[];
}

function buildWarehouseStatusTree(statuses: StatusOption[], filterStoreId: string | null): { storeName: string; storeId: string | null; roots: StatusTreeNode[] }[] {
  const childrenMap = new Map<string, StatusOption[]>();
  const rootStatuses: StatusOption[] = [];

  for (const s of statuses) {
    if (s.parentId) {
      if (!childrenMap.has(s.parentId)) childrenMap.set(s.parentId, []);
      childrenMap.get(s.parentId)!.push(s);
    } else {
      rootStatuses.push(s);
    }
  }

  const toNode = (s: StatusOption): StatusTreeNode => ({
    ...s,
    children: (childrenMap.get(s.id) || []).map(toNode),
  });

  // Filter to show only global statuses and statuses for the given store
  const filteredRoots = rootStatuses.filter(s => s.storeId === null || s.storeId === filterStoreId);

  const storeGroups = new Map<string | null, StatusTreeNode[]>();
  for (const root of filteredRoots) {
    const key = root.storeId;
    if (!storeGroups.has(key)) storeGroups.set(key, []);
    storeGroups.get(key)!.push(toNode(root));
  }

  const result: { storeName: string; storeId: string | null; roots: StatusTreeNode[] }[] = [];
  if (storeGroups.has(null)) {
    result.push({ storeName: 'Globalne (wszystkie sklepy)', storeId: null, roots: storeGroups.get(null)! });
  }
  for (const [storeId, roots] of storeGroups) {
    if (storeId === null) continue;
    const storeName = roots[0]?.store?.name || storeId;
    result.push({ storeName, storeId, roots });
  }
  return result;
}

function WarehouseStatusCheckboxTree({
  groups,
  selectedIds,
  onToggle,
}: {
  groups: { storeName: string; storeId: string | null; roots: StatusTreeNode[] }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
}) {
  const renderNode = (node: StatusTreeNode, depth: number) => (
    <div key={node.id}>
      <label
        className="flex items-center gap-3 py-1.5 px-2 rounded-lg cursor-pointer hover:bg-surface-hover/50 transition-colors"
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
      >
        <div
          onClick={(e) => { e.preventDefault(); onToggle(node.id); }}
          className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            selectedIds.includes(node.id)
              ? 'bg-accent-primary border-accent-primary'
              : 'border-gray-500 hover:border-gray-400'
          }`}
        >
          {selectedIds.includes(node.id) && (
            <CheckCircle className="w-3.5 h-3.5 text-dark-900" />
          )}
        </div>
        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: node.color }} />
        <span className={`text-sm ${selectedIds.includes(node.id) ? 'text-text-primary font-medium' : 'text-text-secondary'}`}>
          {node.name}
        </span>
      </label>
      {node.children.map(child => renderNode(child, depth + 1))}
    </div>
  );

  return (
    <div className="space-y-4 max-h-[300px] overflow-y-auto">
      {groups.map((group) => (
        <div key={group.storeId ?? 'global'}>
          <div className="flex items-center gap-2 mb-2 px-2">
            <Store className="w-3.5 h-3.5 text-text-muted" />
            <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">{group.storeName}</span>
          </div>
          <div className="space-y-0.5">
            {group.roots.map(root => renderNode(root, 0))}
          </div>
        </div>
      ))}
      {groups.length === 0 && (
        <p className="text-sm text-text-muted text-center py-4">Brak statusów do wyświetlenia</p>
      )}
    </div>
  );
}

function WarehouseTab({ storeId, onSettingsChange }: { storeId: string; onSettingsChange?: (settings: WarehouseSettingsData) => void }) {
  const [loading, setLoading] = useState(true);
  const [_saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState<WarehouseSettingsData>({
    productSyncEnabled: false,
    stockSyncDirection: 'wc_to_ecom',
    stockDecreaseStatusIds: [],
    stockIncreaseStatusIds: [],
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [settingsRes, statusesRes] = await Promise.all([
          api.get<WarehouseSettingsData>(`/settings/warehouse/${storeId}`),
          api.get<StatusOption[]>('/statuses'),
        ]);
        setSettings(settingsRes.data);
        setStatuses(statusesRes.data);
        setLoaded(true);
      } catch (err) {
        console.error('Failed to load warehouse settings:', err);
        setError('Nie udało się wczytać ustawień magazynu');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [storeId]);

  // Notify parent when settings change
  useEffect(() => {
    if (loaded) {
      onSettingsChange?.(settings);
    }
  }, [settings, loaded]);

  const handleSyncProducts = async () => {
    setSyncing(true);
    setError('');
    try {
      const { data } = await warehouseApi.syncProductsFromWooCommerce(storeId);
      setSuccess(data.message);
      setTimeout(() => setSuccess(''), 5000);
    } catch {
      setError('Nie udało się pobrać produktów z WooCommerce');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleProductSync = async (enabled: boolean) => {
    const newSettings = { ...settings, productSyncEnabled: enabled };
    setSettings(newSettings);
    setSaving(true);
    setError('');
    try {
      await api.put(`/settings/warehouse/${storeId}`, newSettings);
      setSuccess(enabled ? 'Pobieranie produktów włączone' : 'Pobieranie produktów wyłączone');
      setTimeout(() => setSuccess(''), 3000);
      if (enabled) handleSyncProducts();
    } catch {
      setSettings(prev => ({ ...prev, productSyncEnabled: !enabled }));
      setError('Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  const statusTree = buildWarehouseStatusTree(statuses, storeId);

  const toggleDecrease = (id: string) => {
    setSettings(prev => ({
      ...prev,
      stockDecreaseStatusIds: prev.stockDecreaseStatusIds.includes(id)
        ? prev.stockDecreaseStatusIds.filter(x => x !== id)
        : [...prev.stockDecreaseStatusIds, id],
      stockIncreaseStatusIds: prev.stockIncreaseStatusIds.filter(x => x !== id),
    }));
  };

  const toggleIncrease = (id: string) => {
    setSettings(prev => ({
      ...prev,
      stockIncreaseStatusIds: prev.stockIncreaseStatusIds.includes(id)
        ? prev.stockIncreaseStatusIds.filter(x => x !== id)
        : [...prev.stockIncreaseStatusIds, id],
      stockDecreaseStatusIds: prev.stockDecreaseStatusIds.filter(x => x !== id),
    }));
  };

  const syncDirectionOptions = [
    { value: 'none', label: 'Brak synchronizacji', icon: Ban, description: 'Stany magazynowe nie są śledzone ani synchronizowane' },
    { value: 'wc_to_ecom', label: 'Tylko ecomflow (bez synchronizacji stanów do WC)', icon: ArrowRight, description: 'Stany zmieniają się tylko w ecomflow, WooCommerce nie jest aktualizowany' },
    { value: 'ecom_to_wc', label: 'Wysyłaj stany do WooCommerce', icon: ArrowRight, description: 'Stany magazynowe wysłane do WooCommerce po każdej zmianie', iconFlip: true },
    { value: 'bidirectional', label: 'Pełna synchronizacja obustronna', icon: ArrowLeftRight, description: 'Pełna synchronizacja stanów w obie strony' },
  ];

  const getSelectedNames = (ids: string[]) => {
    return ids.map(id => {
      const s = statuses.find(st => st.id === id);
      return s ? `${s.name}${s.store ? ` (${s.store.name})` : ''}` : id;
    });
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center p-3 bg-danger/10 border border-danger/30 rounded-lg text-danger text-sm">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center p-3 bg-success/10 border border-success/30 rounded-lg text-success text-sm">
          <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          {success}
        </div>
      )}

      {/* Product Sync from WooCommerce */}
      <div className="p-4 bg-surface-hover/30 rounded-xl">
        <h4 className="text-text-primary font-medium mb-2 flex items-center gap-2">
          <Package className="w-4 h-4 text-accent-primary" />
          Pobieranie produktów z WooCommerce
        </h4>
        <p className="text-sm text-text-secondary mb-3">
          Włącz, aby automatycznie pobrać produkty z tego sklepu do magazynu.
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              onClick={() => handleToggleProductSync(!settings.productSyncEnabled)}
              className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                settings.productSyncEnabled ? 'bg-accent-primary' : 'bg-surface-muted'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                settings.productSyncEnabled ? 'left-7' : 'left-1'
              }`} />
            </div>
            <span className="text-sm text-text-primary">
              {settings.productSyncEnabled ? 'Włączone' : 'Wyłączone'}
            </span>
          </div>
          {settings.productSyncEnabled && (
            <button onClick={handleSyncProducts} disabled={syncing} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-surface-muted hover:bg-surface-overlay text-text-primary rounded-lg transition-colors disabled:opacity-50">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {syncing ? 'Synchronizacja...' : 'Synchronizuj'}
            </button>
          )}
        </div>
      </div>

      {settings.productSyncEnabled && (
        <>
          {/* Sync Direction */}
          <div className="p-4 bg-surface-hover/30 rounded-xl">
            <h4 className="text-text-primary font-medium mb-2 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-accent-primary" />
              Kierunek synchronizacji stanów
            </h4>
            <div className="space-y-2">
              {syncDirectionOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => setSettings(prev => ({ ...prev, stockSyncDirection: option.value as WarehouseSettingsData['stockSyncDirection'] }))}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      settings.stockSyncDirection === option.value
                        ? 'border-accent-primary bg-accent-primary/5'
                        : 'border-border hover:border-border-strong bg-surface-hover/30'
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${settings.stockSyncDirection === option.value ? 'bg-accent-primary/20' : 'bg-surface-hover'}`}>
                      <Icon className={`w-4 h-4 ${settings.stockSyncDirection === option.value ? 'text-accent-primary' : 'text-text-secondary'} ${option.iconFlip ? 'rotate-180' : ''}`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${settings.stockSyncDirection === option.value ? 'text-text-primary' : 'text-text-secondary'}`}>
                        {option.label}
                      </p>
                      <p className="text-xs text-text-muted">{option.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stock Decrease Statuses */}
          <div className={`p-4 bg-surface-hover/30 rounded-xl transition-opacity ${settings.stockSyncDirection === 'none' ? 'opacity-40 pointer-events-none' : ''}`}>
            <label className="text-text-primary font-medium mb-1 block">Statusy zmniejszające stan</label>
            <p className="text-xs text-text-muted mb-3">
              {settings.stockSyncDirection === 'none'
                ? 'Niedostępne przy wyłączonej synchronizacji stanów.'
                : 'Gdy zamówienie osiągnie którykolwiek z zaznaczonych statusów, stany produktów automatycznie zmaleją.'}
            </p>
            {settings.stockDecreaseStatusIds.length > 0 && settings.stockSyncDirection !== 'none' && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {getSelectedNames(settings.stockDecreaseStatusIds).map((name, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-md bg-danger/15 text-danger text-xs">{name}</span>
                ))}
              </div>
            )}
            {settings.stockSyncDirection !== 'none' && (
              <WarehouseStatusCheckboxTree groups={statusTree} selectedIds={settings.stockDecreaseStatusIds} onToggle={toggleDecrease} />
            )}
          </div>

          {/* Stock Increase Statuses */}
          <div className={`p-4 bg-surface-hover/30 rounded-xl transition-opacity ${settings.stockSyncDirection === 'none' ? 'opacity-40 pointer-events-none' : ''}`}>
            <label className="text-text-primary font-medium mb-1 block">Statusy zwiększające stan</label>
            <p className="text-xs text-text-muted mb-3">
              {settings.stockSyncDirection === 'none'
                ? 'Niedostępne przy wyłączonej synchronizacji stanów.'
                : 'Gdy zamówienie osiągnie którykolwiek z zaznaczonych statusów, stany produktów automatycznie wzrosną (np. zwrot).'}
            </p>
            {settings.stockIncreaseStatusIds.length > 0 && settings.stockSyncDirection !== 'none' && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {getSelectedNames(settings.stockIncreaseStatusIds).map((name, i) => (
                  <span key={i} className="px-2 py-0.5 rounded-md bg-success/15 text-success text-xs">{name}</span>
                ))}
              </div>
            )}
            {settings.stockSyncDirection !== 'none' && (
              <WarehouseStatusCheckboxTree groups={statusTree} selectedIds={settings.stockIncreaseStatusIds} onToggle={toggleIncrease} />
            )}
          </div>

        </>
      )}
    </div>
  );
}

function StoreSettingsModal({
  store,
  onClose,
  onSuccess,
}: {
  store: StoreType;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const queryClient = useQueryClient();
  const confirmDialog = useConfirmDialog();
  const { showSuccess, showError } = useNotification();
  // View state: 'landing' shows module boxes, 'logistyka'/'engine' show sub-settings
  const [activeView, setActiveView] = useState<'landing' | 'logistyka' | 'engine'>('landing');
  const [activeTab, setActiveTab] = useState<'general' | 'statuses' | 'shipping' | 'warehouse'>('general');
  const [loading, setLoading] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(true);
  const [error, setError] = useState('');
  const [ecomflowStatuses, setEcomflowStatuses] = useState<EcomflowStatus[]>([]);
  const [warehouseSettings, setWarehouseSettings] = useState<WarehouseSettingsData | null>(null);
  const [engineApiKey, setEngineApiKey] = useState<string | null>(null);
  const [engineLoading, setEngineLoading] = useState(false);
  const [engineUpdating, setEngineUpdating] = useState(false);
  const [engineIntId, setEngineIntId] = useState<string | null>(store.engineIntegrationId ?? null);
  const [engineActive, setEngineActive] = useState(store.engineEnabled ?? false);

  // Logistyka module enabled state — derived from whether sync was previously configured
  const [logistykaEnabled, setLogistykaEnabled] = useState(
    store.syncEnabled || !!(store.settings as Record<string, unknown>)?.syncToStatus
  );

  // Initialize settings from store.settings (loaded from database)
  const [settings, setSettings] = useState<LocalStoreSettings>(() => {
    const savedSettings = store.settings || {};
    return {
      syncEnabled: store.syncEnabled,
      syncFromDate: savedSettings.syncFromDate || '',
      syncToStatus: savedSettings.syncToStatus || '',
      syncStatuses: savedSettings.syncStatuses || ['pending', 'processing', 'on-hold'],
      statusMappings: savedSettings.statusMappings || [],
      defaultManualOrderStatusId: savedSettings.defaultManualOrderStatusId || null,
      shippingMethodMappings: savedSettings.shippingMethodMappings || [],
    };
  });

  // Fetch user-created statuses (tree structure)
  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        setLoadingStatuses(true);
        const response = await api.get<EcomflowStatus[]>('/statuses/tree');
        setEcomflowStatuses(response.data);
      } catch (err) {
        console.error('Failed to fetch statuses:', err);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchStatuses();
  }, []);

  // Flatten statuses for dropdown with parent path
  const flattenStatuses = (statuses: EcomflowStatus[]): { id: string; name: string; level: number; parentName?: string; color: string }[] => {
    const result: { id: string; name: string; level: number; parentName?: string; color: string }[] = [];
    const addStatus = (status: EcomflowStatus, level: number, parentName?: string) => {
      result.push({ id: status.id, name: status.name, level, parentName, color: status.color });
      status.children?.forEach(child => addStatus(child, level + 1, status.name));
    };
    statuses.forEach(s => addStatus(s, 0));
    return result;
  };

  const allEcomflowStatuses = flattenStatuses(ecomflowStatuses);

  // Update status mapping
  const updateStatusMapping = (wooStatus: string, ecomflowStatus: string) => {
    setSettings(prev => {
      const existing = prev.statusMappings.find(m => m.wooStatus === wooStatus);
      if (existing) {
        if (!ecomflowStatus) {
          return { ...prev, statusMappings: prev.statusMappings.filter(m => m.wooStatus !== wooStatus) };
        }
        return { ...prev, statusMappings: prev.statusMappings.map(m => m.wooStatus === wooStatus ? { ...m, ecomflowStatus } : m) };
      } else if (ecomflowStatus) {
        return { ...prev, statusMappings: [...prev.statusMappings, { wooStatus, ecomflowStatus }] };
      }
      return prev;
    });
  };

  const getStatusMappingValue = (wooStatus: string): string => {
    return settings.statusMappings.find(m => m.wooStatus === wooStatus)?.ecomflowStatus || '';
  };

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeView !== 'landing') { setActiveView('landing'); setError(''); }
        else onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, activeView]);

  const handleSubmit = async () => {
    setError('');

    // Validate required status fields when Logistyka module is enabled
    if (logistykaEnabled) {
      if (!settings.syncToStatus) {
        setError('Wybierz domyślny status zamówień w Ecomflow - jest to pole wymagane');
        setActiveView('logistyka');
        setActiveTab('general');
        return;
      }
      if (!settings.defaultManualOrderStatusId) {
        setError('Wybierz domyślny status zamówień ręcznych - jest to pole wymagane');
        setActiveView('logistyka');
        setActiveTab('general');
        return;
      }
    }

    // Validate syncFromDate is not in the future
    if (settings.syncFromDate && new Date(settings.syncFromDate) > new Date()) {
      setError('Data synchronizacji nie może być w przyszłości');
      return;
    }

    setLoading(true);

    try {
      // If Logistyka module is disabled, force syncEnabled off
      const effectiveSyncEnabled = logistykaEnabled ? settings.syncEnabled : false;

      await storeApi.update(store.id, {
        syncEnabled: effectiveSyncEnabled,
        settings: {
          syncToStatus: settings.syncToStatus,
          syncStatuses: settings.syncStatuses,
          statusMappings: settings.statusMappings,
          syncFromDate: settings.syncFromDate || null,
          defaultManualOrderStatusId: settings.defaultManualOrderStatusId,
          shippingMethodMappings: settings.shippingMethodMappings,
        },
      });
      if (warehouseSettings) {
        await api.put(`/settings/warehouse/${store.id}`, warehouseSettings);
      }
      onSuccess();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zapisać ustawień');
    } finally {
      setLoading(false);
    }
  };

  const toggleSyncStatus = (statusId: string) => {
    setSettings(prev => ({
      ...prev,
      syncStatuses: prev.syncStatuses.includes(statusId)
        ? prev.syncStatuses.filter(s => s !== statusId)
        : [...prev.syncStatuses, statusId]
    }));
  };

  const logistykaTabs = [
    { id: 'general' as const, name: 'Ogólne' },
    { id: 'statuses' as const, name: 'Statusy' },
    { id: 'shipping' as const, name: 'Wysyłka' },
    { id: 'warehouse' as const, name: 'Magazyn' },
  ];

  // Toggle switch component
  const ToggleSwitch = ({ enabled, onToggle, disabled }: { enabled: boolean; onToggle: () => void; disabled?: boolean }) => (
    <button
      onClick={(e) => { e.stopPropagation(); onToggle(); }}
      disabled={disabled}
      className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
        enabled ? 'bg-success' : 'bg-surface-muted'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${enabled ? 'left-7' : 'left-1'}`} />
    </button>
  );

  return (
    <div
      className="fixed inset-0 bg-surface-base/95 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in z-50"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`bg-surface-raised border border-border rounded-2xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-[max-width] ${activeView === 'engine' ? 'max-w-3xl' : 'max-w-2xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-subtle">
          <div className="flex items-center gap-3">
            {activeView !== 'landing' && (
              <button
                onClick={() => { setActiveView('landing'); setError(''); }}
                className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                {activeView === 'landing' && 'Ustawienia integracji'}
                {activeView === 'logistyka' && 'Logistyka'}
                {activeView === 'engine' && 'EcomFlow Engine'}
              </h2>
              <p className="text-sm text-text-secondary">{store.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          {error && (
            <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger mb-4">
              <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* ═══════════════ LANDING VIEW ═══════════════ */}
          {activeView === 'landing' && (
            <div className="space-y-4">
              {/* Logistyka Box */}
              <div className="p-5 rounded-xl border border-border bg-surface-hover/20">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Truck className="w-5 h-5 text-accent-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-text-primary font-semibold">Logistyka</h3>
                      <p className="text-xs text-text-muted mt-1">
                        Pobieranie zamówień, statusy, mapowanie, wysyłka i magazyn
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch enabled={logistykaEnabled} onToggle={() => setLogistykaEnabled(prev => !prev)} />
                </div>
                {logistykaEnabled && (
                  <div className="mt-4 pt-4 border-t border-border-subtle">
                    <button
                      onClick={() => setActiveView('logistyka')}
                      className="btn-secondary text-sm flex items-center gap-2 w-full justify-center"
                    >
                      <Settings className="w-4 h-4" />
                      Ustawienia logistyki
                    </button>
                  </div>
                )}
              </div>

              {/* Engine Box */}
              <div className="p-5 rounded-xl border border-border bg-surface-hover/20">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-accent-secondary/10 border border-accent-secondary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Puzzle className="w-5 h-5 text-accent-secondary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-text-primary font-semibold">EcomFlow Engine</h3>
                      <p className="text-xs text-text-muted mt-1">
                        Kreator wyglądu sklepu — styl, kolory, typografia, layout i szablony stron
                      </p>
                    </div>
                  </div>
                  <ToggleSwitch
                    enabled={engineActive}
                    disabled={engineLoading}
                    onToggle={async () => {
                      if (engineActive) {
                        if (!(await confirmDialog({
                          title: 'Wyłącz Engine',
                          message: 'Czy na pewno chcesz wyłączyć Engine? Ustawienia kreatora zostaną zachowane.',
                          confirmLabel: 'Wyłącz',
                        }))) return;
                        setEngineLoading(true);
                        try {
                          await storeApi.disableEngine(store.id);
                          setEngineActive(false);
                          setEngineApiKey(null);
                          setEngineIntId(null);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Nie udało się wyłączyć Engine');
                        } finally {
                          setEngineLoading(false);
                        }
                      } else {
                        setEngineLoading(true);
                        try {
                          const { data } = await storeApi.enableEngine(store.id);
                          setEngineApiKey(data.apiKey);
                          setEngineIntId(data.integrationId);
                          setEngineActive(true);
                        } catch (err) {
                          setError(err instanceof Error ? err.message : 'Nie udało się włączyć Engine');
                        } finally {
                          setEngineLoading(false);
                        }
                      }
                    }}
                  />
                </div>
                {engineActive && (
                  <div className="mt-4 pt-4 border-t border-border-subtle">
                    <button
                      onClick={() => setActiveView('engine')}
                      className="btn-secondary text-sm flex items-center gap-2 w-full justify-center"
                    >
                      <Settings className="w-4 h-4" />
                      Ustawienia Engine
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ═══════════════ LOGISTYKA VIEW ═══════════════ */}
          {activeView === 'logistyka' && (
            <>
              {/* Tabs */}
              <div className="flex gap-1 p-1 bg-surface-hover/50 rounded-lg mb-6">
                {logistykaTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                      activeTab === tab.id ? 'bg-accent-primary text-dark-900' : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {tab.name}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    {/* Auto Sync Toggle */}
                    <div className="flex items-center justify-between p-4 bg-surface-hover/30 rounded-xl">
                      <div>
                        <h4 className="text-text-primary font-medium">Automatyczne pobieranie zamówień</h4>
                        <p className="text-sm text-text-secondary">Włącz automatyczne pobieranie zamówień z tego sklepu</p>
                      </div>
                      <ToggleSwitch
                        enabled={settings.syncEnabled}
                        onToggle={() => setSettings(prev => ({ ...prev, syncEnabled: !prev.syncEnabled }))}
                      />
                    </div>

                    {/* Sync From Date */}
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <label className="label">Pobieraj zamówienia od daty</label>
                      <input
                        type="date"
                        value={settings.syncFromDate}
                        onChange={(e) => setSettings(prev => ({ ...prev, syncFromDate: e.target.value }))}
                        max={new Date().toISOString().split('T')[0]}
                        className="input"
                      />
                      {settings.syncFromDate && new Date(settings.syncFromDate) > new Date() && (
                        <p className="text-xs text-danger mt-2">
                          Data nie może być w przyszłości! Synchronizacja nie pobierze żadnych zamówień.
                        </p>
                      )}
                      <p className="text-xs text-text-muted mt-2">
                        Pozostaw puste, aby pobierać wszystkie zamówienia
                      </p>
                    </div>

                    {/* Sync To Status */}
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <label className="label">Domyślny status zamówień w Ecomflow <span className="text-danger">*</span></label>
                      {loadingStatuses ? (
                        <div className="flex items-center gap-2 py-3 text-text-secondary">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Ładowanie statusów...</span>
                        </div>
                      ) : (
                        <select
                          value={settings.syncToStatus}
                          onChange={(e) => setSettings(prev => ({ ...prev, syncToStatus: e.target.value }))}
                          className={`input ${!settings.syncToStatus ? 'border-red-400/50' : ''}`}
                        >
                          <option value="">Wybierz status...</option>
                          {allEcomflowStatuses.map(status => (
                            <option key={status.id} value={status.id}>
                              {status.level > 0
                                ? `${'    '.repeat(status.level - 1)}└─ ${status.parentName} → ${status.name}`
                                : `● ${status.name}`
                              }
                            </option>
                          ))}
                        </select>
                      )}
                      <p className="text-xs text-text-muted mt-2">
                        Wybierz, do jakiego statusu w Ecomflow mają trafiać pobrane zamówienia.
                        {allEcomflowStatuses.length === 0 && !loadingStatuses && (
                          <span className="block text-warning mt-1">
                            Nie masz jeszcze żadnych statusów. Utwórz je w Administracja → Statusy zamówień.
                          </span>
                        )}
                      </p>
                    </div>

                    {/* Default Manual Order Status */}
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <label className="label">
                        Domyślny status zamówień ręcznych <span className="text-danger">*</span>
                      </label>
                      <p className="text-xs text-text-muted mb-3">
                        Status automatycznie przypisywany do ręcznie tworzonych zamówień w tym sklepie.
                      </p>
                      {loadingStatuses ? (
                        <div className="flex items-center gap-2 py-3 text-text-secondary">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Ładowanie statusów...</span>
                        </div>
                      ) : (
                        <select
                          value={settings.defaultManualOrderStatusId || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, defaultManualOrderStatusId: e.target.value || null }))}
                          className={`input w-full ${!settings.defaultManualOrderStatusId ? 'border-red-400/50' : ''}`}
                        >
                          <option value="">— Wybierz status —</option>
                          {allEcomflowStatuses.map(status => (
                            <option key={status.id} value={status.id}>
                              {status.level > 0
                                ? `${'    '.repeat(status.level - 1)}└─ ${status.parentName} → ${status.name}`
                                : `● ${status.name}`
                              }
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'statuses' && (
                  <div className="space-y-4">
                    {/* Sync Statuses */}
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <h4 className="text-text-primary font-medium mb-2">Pobieraj zamówienia ze statusami WooCommerce</h4>
                      <p className="text-sm text-text-secondary mb-4">
                        Wybierz, które statusy WooCommerce mają być synchronizowane
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {WOO_STATUSES.map(status => (
                          <label
                            key={status.id}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              settings.syncStatuses.includes(status.id)
                                ? 'bg-accent-primary/20 border border-accent-primary/50'
                                : 'bg-surface-muted/50 border border-transparent hover:bg-surface-muted'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={settings.syncStatuses.includes(status.id)}
                              onChange={() => toggleSyncStatus(status.id)}
                              className="sr-only"
                            />
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                              settings.syncStatuses.includes(status.id) ? 'bg-accent-primary border-accent-primary' : 'border-gray-500'
                            }`}>
                              {settings.syncStatuses.includes(status.id) && (
                                <CheckCircle className="w-3 h-3 text-dark-900" />
                              )}
                            </div>
                            <span className="text-sm text-text-primary font-mono">{status.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Status Mapping */}
                    <div className="p-4 bg-surface-hover/30 rounded-xl">
                      <h4 className="text-text-primary font-medium mb-2">Mapowanie statusów</h4>
                      <p className="text-sm text-text-secondary mb-4">
                        Przypisz statusy WooCommerce do statusów Ecomflow. Gdy zmienisz status zamówienia w Ecomflow,
                        zostanie on automatycznie zaktualizowany w WooCommerce zgodnie z mapowaniem.
                      </p>
                      {loadingStatuses ? (
                        <div className="flex items-center gap-2 py-3 text-text-secondary">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span className="text-sm">Ładowanie statusów...</span>
                        </div>
                      ) : allEcomflowStatuses.length === 0 ? (
                        <EmptyState
                          icon={AlertCircle}
                          title="Brak statusów"
                          description="Utwórz statusy w Administracja → Statusy zamówień, aby skonfigurować mapowanie."
                          size="sm"
                        />
                      ) : (
                        <div className="space-y-3">
                          {WOO_STATUSES.map(wooStatus => (
                            <div key={wooStatus.id} className="flex items-center gap-4 p-3 bg-surface-muted/30 rounded-lg">
                              <div className="flex-1">
                                <span className="text-sm text-text-primary font-mono">{wooStatus.name}</span>
                                <span className="text-xs text-text-muted ml-2">(WooCommerce)</span>
                              </div>
                              <span className="text-text-muted">→</span>
                              <div className="flex-1">
                                <select
                                  value={getStatusMappingValue(wooStatus.id)}
                                  onChange={(e) => updateStatusMapping(wooStatus.id, e.target.value)}
                                  className="input !py-2 text-sm"
                                >
                                  <option value="">— Nie mapuj —</option>
                                  {allEcomflowStatuses.map(status => (
                                    <option key={status.id} value={status.name}>
                                      {status.level > 0
                                        ? `${'    '.repeat(status.level - 1)}└─ ${status.parentName} → ${status.name}`
                                        : `● ${status.name}`
                                      }
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-text-muted mt-4">
                        Dzięki mapowaniu możesz utworzyć dedykowany status np. &quot;Sklep ABC - Nowe&quot; i przypisać go do &quot;pending&quot; z WooCommerce.
                      </p>
                    </div>
                  </div>
                )}

                {activeTab === 'shipping' && (
                  <ShippingMappingTab
                    storeId={store.id}
                    mappings={settings.shippingMethodMappings}
                    onMappingsChange={(mappings) => setSettings(prev => ({ ...prev, shippingMethodMappings: mappings }))}
                  />
                )}

                <div style={{ display: activeTab === 'warehouse' ? undefined : 'none' }}>
                  <WarehouseTab storeId={store.id} onSettingsChange={setWarehouseSettings} />
                </div>
              </div>
            </>
          )}

          {/* ═══════════════ ENGINE VIEW ═══════════════ */}
          {activeView === 'engine' && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
                  Engine aktywny
                </span>
              </div>

              {/* API Key */}
              {engineApiKey && (
                <div className="p-3 bg-surface-hover/50 rounded-lg border border-accent-primary/20">
                  <p className="text-xs text-text-muted mb-1">Klucz API (zapisz go — nie będzie wyświetlony ponownie)</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm text-accent-primary font-mono break-all flex-1">{engineApiKey}</code>
                    <button
                      onClick={() => { navigator.clipboard.writeText(engineApiKey); }}
                      className="p-2 rounded-lg text-text-secondary hover:text-accent-primary hover:bg-surface-muted transition-all flex-shrink-0"
                      title="Kopiuj"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className="p-3 bg-surface-hover/30 rounded-lg text-xs text-text-muted space-y-1">
                <p>1. Pobierz i zainstaluj wtyczkę EcomFlow Engine w WordPressie</p>
                <p>2. Wklej klucz API w WordPressie → Wygląd → EcomFlow Engine</p>
                <p>3. Otwórz Kreator aby konfigurować wygląd sklepu</p>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 md:flex-nowrap [&>*]:whitespace-nowrap">
                <button
                  onClick={async () => {
                    setEngineLoading(true);
                    try {
                      const { data } = await storeApi.regenerateEngineKey(store.id);
                      setEngineApiKey(data.apiKey);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Nie udało się wygenerować klucza');
                    } finally {
                      setEngineLoading(false);
                    }
                  }}
                  disabled={engineLoading}
                  className="btn-secondary text-xs flex items-center gap-1.5"
                >
                  <Key className="w-3.5 h-3.5" />
                  Regeneruj klucz API
                </button>
                <button
                  onClick={async () => {
                    try {
                      const response = await engineApi.downloadTheme();
                      const blob = new Blob([response.data], { type: 'application/zip' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'ecomflow-engine.zip';
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch { /* ignore */ }
                  }}
                  className="btn-secondary text-xs flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" />
                  Pobierz wtyczkę
                </button>
                {engineIntId && (
                  <button
                    onClick={async () => {
                      if (!(await confirmDialog({
                        title: 'Aktualizuj EcomFlow Engine',
                        message: 'WordPress pobierze i zainstaluje najnowszą oficjalną wersję EcomFlow Engine. Sklep może być przez chwilę w trybie aktualizacji.',
                        confirmLabel: 'Aktualizuj',
                      }))) return;

                      setEngineUpdating(true);
                      try {
                        const { data } = await engineApi.updateTheme(engineIntId);
                        void queryClient.invalidateQueries({ queryKey: ['engine-theme-status'] });
                        showSuccess('engine-update', {
                          message: data.updated
                            ? `EcomFlow Engine został zaktualizowany${data.currentVersion ? ` do wersji ${data.currentVersion}` : ''}.`
                            : data.message,
                        });
                      } catch (err) {
                        showError(err, 'engine-update');
                      } finally {
                        setEngineUpdating(false);
                      }
                    }}
                    disabled={engineUpdating || engineLoading}
                    className="btn-secondary text-xs flex items-center gap-1.5"
                  >
                    {engineUpdating
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
                    {engineUpdating ? 'Aktualizowanie...' : 'Aktualizuj wtyczkę'}
                  </button>
                )}
                {engineIntId && (
                  <a
                    href={`/kreator/${engineIntId}/pages`}
                    className="btn-secondary text-xs flex items-center gap-1.5"
                  >
                    <Settings className="w-3.5 h-3.5" />
                    Otwórz Kreator
                  </a>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border-subtle">
          {activeView === 'landing' ? (
            <>
              <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Zapisywanie...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Zapisz
                  </span>
                )}
              </button>
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Anuluj
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSubmit} disabled={loading} className="btn-primary flex-1">
                {loading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Zapisywanie...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Zapisz ustawienia
                  </span>
                )}
              </button>
              <button type="button" onClick={() => { setActiveView('landing'); setError(''); }} className="btn-secondary flex-1">
                Wróć
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
